import { Button } from "@/components/ui/buttons/button";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { CaretIcon, HeartIcon } from "@/icons";
import { useMemo, useState, useEffect } from "react";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { RouteNames } from "@/types/route-names";
import {
  PlayerBalanceOutput as PlayerBalance,
  InventoryPokemonsOutput,
  BreedOutput,
} from "types/web-app";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { calculateBreedCost } from "@/services/breeding/calculate-breed-cost";
import { breed } from "@/services/breeding/breed";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { Alert } from "@/components/alert/alert";
import cn from "classnames";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { PokemonTalent } from "@/components/pokemon/pokemon-talent";
import { LoadingOverlay } from "@/components/ui/overlays/loading-overlay";

const MAX_BREED_COUNT = 4;

type ParentPokemon = InventoryPokemonsOutput["player_pokemons"][number];
type EggType = BreedOutput["eggs"][number];

const BreedingSequence = ({
  parent1,
  parent2,
  eggs,
  onComplete,
}: {
  parent1: ParentPokemon;
  parent2: ParentPokemon;
  eggs: EggType[];
  onComplete: () => void;
}) => {
  const [phase, setPhase] = useState<
    "attract" | "romance" | "flash" | "reveal" | "summary"
  >("attract");

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === "attract") {
      // Move parents to center
      timeout = setTimeout(() => {
        setPhase("romance");
      }, 1000);
    } else if (phase === "romance") {
      // Play kiss animation
      timeout = setTimeout(() => {
        setPhase("flash");
      }, 1500);
    } else if (phase === "flash") {
      // Flash white
      timeout = setTimeout(() => {
        setPhase("reveal");
      }, 500);
    } else if (phase === "reveal") {
      // Show eggs
      timeout = setTimeout(() => {
        setPhase("summary");
      }, 2000);
    }

    return () => clearTimeout(timeout);
  }, [phase]);

  if (phase === "summary") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xl animate-fadeIn">
        <StandardFrame
          className="w-full max-w-md overflow-visible shadow-cartoon-lg"
          variant="beige"
        >
          <div className="flex flex-col items-center p-6 gap-6 relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-pk-pink-1/20 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <StrokedText
                fontSize={32}
                strokeWidth={8}
                className="text-pk-yellow-1 mb-2"
              >
                Breeding Complete!
              </StrokedText>
              <StrokedText
                fontSize={16}
                strokeWidth={4}
                className="text-pk-gray-4"
              >
                {`You received ${eggs.length} new egg${
                  eggs.length > 1 ? "s" : ""
                }!`}
              </StrokedText>
            </div>

            <div className="grid grid-cols-2 gap-4 w-full justify-center">
              {eggs.map((egg, idx) => (
                <div
                  key={egg.id}
                  className="relative z-10 p-4 bg-pk-beige-2/50 rounded-xl border-2 border-pk-white/20 flex flex-col items-center gap-2 animate-[fadeIn_0.5s_ease-out_both]"
                  style={{ animationDelay: `${idx * 0.2}s` }}
                >
                  <img
                    src={`/assets/eggs/${egg.base_pokemon_name}.png`}
                    alt="Egg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/assets/misc/poke-egg.png";
                    }}
                    className="w-16 h-auto rendering-pixelated drop-shadow-cartoon-sm"
                  />
                  <span className="text-pk-gray-4 font-bold text-sm">
                    #{egg.display_id}
                  </span>
                </div>
              ))}
            </div>

            <Button
              variant="green"
              onClick={onComplete}
              className="w-full mt-4 shadow-cartoon-green z-10"
            >
              <span className="font-bold text-lg">Collect Eggs</span>
            </Button>
          </div>
        </StandardFrame>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xl overflow-hidden">
      {/* Flash Overlay */}
      <div
        className={`absolute inset-0 bg-white pointer-events-none transition-opacity duration-500 z-50 ${
          phase === "flash" ? "opacity-100" : "opacity-0"
        }`}
      />

      <div className="relative w-full h-full flex items-center justify-center">
        {/* Parents Animation */}
        {phase !== "reveal" && (
          <div className="relative w-full max-w-lg h-64 flex items-center justify-center">
            {/* Parent 1 (Left) */}
            <div
              className="absolute transition-all duration-1000 ease-in-out z-10"
              style={{
                left: phase === "attract" ? "10%" : "35%",
                opacity: phase === "flash" ? 0 : 1,
                transform: "translateX(-50%)",
              }}
            >
              <PokemonSprite
                pokemon={parent1}
                style={{ scale: 4 }}
                className="drop-shadow-cartoon-lg"
              />
            </div>

            {/* Parent 2 (Right) */}
            <div
              className="absolute transition-all duration-1000 ease-in-out z-10"
              style={{
                right: phase === "attract" ? "10%" : "35%",
                opacity: phase === "flash" ? 0 : 1,
                transform: "translateX(50%) ", // Flip horizontally
              }}
            >
              <PokemonSprite
                pokemon={parent2}
                style={{ scale: 4 }}
                className="drop-shadow-cartoon-lg"
              />
            </div>

            {/* Heart VFX */}
            {phase === "romance" && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <AnimatedVFX
                  id="breeding-kiss"
                  spriteUrl="/assets/spells/projectile/sweet-kiss.png"
                  position={{ x: 0, y: 0 }}
                  style={{
                    transform: "scale(4)",
                  }}
                  loops={3}
                  speed={0.5}
                />
              </div>
            )}
          </div>
        )}

        {/* Reveal Eggs */}
        {phase === "reveal" && (
          <div className="relative flex flex-col items-center animate-fadeIn">
            <AnimatedVFX
              id="reveal-sparkles"
              spriteUrl="/assets/spells/self/self_sparkles_006__50.png"
              position={{ x: 0, y: 0 }}
              style={{
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%) scale(4)",
              }}
              loops={1}
              speed={0.5}
            />
            <div className="flex gap-4 items-center justify-center">
              {eggs.map((egg, idx) => (
                <div
                  key={idx}
                  className="animate-[bounce_1s_infinite]"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <img
                    src={`/assets/eggs/${egg.base_pokemon_name}.png`}
                    alt="Egg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/assets/misc/poke-egg.png";
                    }}
                    className="w-24 h-auto rendering-pixelated drop-shadow-[0_0_15px_rgba(255,255,255,0.6)]"
                  />
                </div>
              ))}
            </div>
            <div className="mt-12">
              <StrokedText
                fontSize={32}
                strokeWidth={6}
                className="text-pk-yellow-3 animate-pulse"
              >
                It's a match!
              </StrokedText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BreedView = () => {
  const { selectedPokemons, setSelectedPokemons, setCurrentRoute, setIntent } =
    useSPAContext();
  const { getAccessToken } = useAuth();
  const { pokeCoins, setPokeCoins, pokeShards, setPokeShards } = useUser();

  const [amount, setAmount] = useState<number>(1);
  const [breedCost, setBreedCost] = useState<{
    costs: Array<{ coin: number; shard: number } | null>;
    parent1BreedCount: number;
    parent2BreedCount: number;
  } | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [isBreeding, setIsBreeding] = useState<boolean>(false);
  const [viewState, setViewState] = useState<"selection" | "breeding">(
    "selection",
  );
  const [bredEggs, setBredEggs] = useState<EggType[]>([]);
  const [hasConfirmedRelease, setHasConfirmedRelease] = useState<boolean>(false);

  const { parent1, parent2 } = useMemo(() => {
    return {
      parent1: selectedPokemons[0],
      parent2: selectedPokemons[1],
    };
  }, [selectedPokemons]);

  const isShinyBreed = useMemo(() => {
    return parent1?.is_shiny || parent2?.is_shiny;
  }, [parent1, parent2]);

  // Reset confirmation if parents change
  useEffect(() => {
    setHasConfirmedRelease(false);
  }, [parent1, parent2]);

  const maxAvailableAmount = useMemo(() => {
    if (!breedCost) return 1;
    return breedCost.costs.filter((c) => c !== null).length;
  }, [breedCost]);

  // Reset amount if it exceeds max available
  useEffect(() => {
    if (amount > maxAvailableAmount) {
      setAmount(Math.max(1, maxAvailableAmount));
    }
  }, [maxAvailableAmount, amount]);

  const currentTotalCost = useMemo(() => {
    if (!breedCost) return { coin: 0, shard: 0 };
    let coin = 0;
    let shard = 0;
    for (let i = 0; i < amount; i++) {
      const cost = breedCost.costs[i];
      if (cost) {
        coin += cost.coin;
        shard += cost.shard;
      }
    }
    return { coin, shard };
  }, [breedCost, amount]);

  // Fetch player balance on mount
  useEffect(() => {
    const fetchBalance = async () => {
      const accessToken = getAccessToken();
      if (!accessToken) return;

      try {
        const response = await getPlayerProfile(accessToken);
        if (response.ok && response.data) {
          setPokeCoins(response.data.poke_coins);
          setPokeShards(response.data.poke_shards);
        }
      } catch (error) {
        console.error("Failed to fetch player balance:", error);
      }
    };

    void fetchBalance();
  }, [getAccessToken, setPokeCoins, setPokeShards]);

  // Calculate breed cost when parents are selected
  useEffect(() => {
    const calculateCost = async () => {
      if (!parent1 || !parent2) return;

      const accessToken = getAccessToken();
      if (!accessToken) {
        Alert("Authentication error. Please try again");
        return;
      }

      setIsCalculating(true);
      try {
        const response = await calculateBreedCost(accessToken, {
          parent1_id: parent1.id,
          parent2_id: parent2.id,
        });

        if (response.ok && response.data) {
          setBreedCost({
            costs: response.data.costs,
            parent1BreedCount: response.data.parent1_breed_count,
            parent2BreedCount: response.data.parent2_breed_count,
          });
        } else {
          Alert(response.message || "Failed to calculate breed cost");
        }
      } catch (error) {
        console.error("Failed to calculate breed cost:", error);
        Alert("Failed to calculate breed cost");
      } finally {
        setIsCalculating(false);
      }
    };

    void calculateCost();
  }, [parent1, parent2, getAccessToken]);

  const handleBreed = async () => {
    if (!parent1 || !parent2 || !breedCost || isBreeding) return;

    if (isShinyBreed && !hasConfirmedRelease) {
      Alert("Please confirm that you understand the shiny breeding consequences.");
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsBreeding(true);
    try {
      const response = await breed(accessToken, {
        parent1_id: parent1.id,
        parent2_id: parent2.id,
        amount: amount,
      });

      if (response.ok && response.data) {
        // Update balance
        setPokeCoins((prev) => prev - response.data!.coin_cost);
        setPokeShards((prev) => (prev ?? 0) - response.data!.shard_cost);

        // Start breeding sequence
        setBredEggs(response.data.eggs);
        setViewState("breeding");
      } else {
        Alert(response.message || "Failed to breed");
      }
    } catch (error) {
      console.error("Failed to breed:", error);
      Alert("Failed to breed");
    } finally {
      setIsBreeding(false);
    }
  };

  const handleFinishBreeding = () => {
    setSelectedPokemons([]);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
  };

  const handleCancel = () => {
    setSelectedPokemons([]);
    setCurrentRoute(undefined);
  };

  if (selectedPokemons.length !== 2) {
    setIntent(Intent.SELECTING_POKEMONS_TO_BREED);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
  }

  if (viewState === "breeding" && parent1 && parent2) {
    return (
      <BreedingSequence
        parent1={parent1}
        parent2={parent2}
        eggs={bredEggs}
        onComplete={handleFinishBreeding}
      />
    );
  }

  const hasInsufficientCoins = !!(
    breedCost &&
    pokeCoins < currentTotalCost.coin
  );
  const hasInsufficientShards = !!(
    breedCost &&
    (pokeShards ?? 0) < currentTotalCost.shard
  );
  const hasInsufficientFunds = hasInsufficientCoins || hasInsufficientShards;

  const parent1ReachedLimit =
    (breedCost?.parent1BreedCount ?? 0) >= MAX_BREED_COUNT;
  const parent2ReachedLimit =
    (breedCost?.parent2BreedCount ?? 0) >= MAX_BREED_COUNT;
  const isBreedingBlocked = parent1ReachedLimit || parent2ReachedLimit;

  return (
    <section className="flex flex-col w-full h-full overflow-hidden">
      {/* Header */}
      <div className="relative flex items-center justify-between w-full px-4 pt-4 shrink-0">
        <div className="absolute h-1/2 w-full left-0 top-1/2 -translate-y-1/2 bg-pk-gray-1"></div>
        <StrokedText strokeWidth={12} fontSize={24} className="z-10">
          BREEDING
        </StrokedText>
      </div>

      <div className="mx-4 flex-1 min-h-0 overflow-y-auto">
        <StandardFrame
          variant="black"
          className={cn(
            "shadow-cartoon min-w-2/3 h-fit transition-all duration-500",
            {
              "!border-pk-yellow-3 shadow-[0_0_30px_rgba(255,215,0,0.3)]":
                isShinyBreed,
            },
          )}
        >
          <div className="flex flex-col  w-full backdrop-blur-[2px] z-40 rounded-2xl">
            <div className=" grid grid-cols-2 items-center justify-between gap-1 relative mb-2">
              {/* Parent 1 */}
              {parent1 ? (
                <div className=" relative flex z-10 flex-col h-full items-start gap-2 overflow-visible   grow">
                  <div className="relative rounded-sm  h-[150px] flex w-full  items-center justify-center">
                    <PokemonSprite
                      pokemon={parent1}
                      style={{ scale: 3 }}
                      className="mt-auto z-20 translate-y-[-5px]"
                    />
                    <div className="absolute top-0 left-0">
                      <StrokedText
                        fontSize={22}
                        strokeWidth={0}
                        fillColor="#ead7d1"
                        className="ml-2 my-1"
                      >
                        {`#${parent1.display_id}`}
                      </StrokedText>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-center w-full">
                    <span className="text-pk-gray-3 text-xs">BREED COUNT</span>
                    <span className="font-semibold text-pk-gray-4">
                      {breedCost?.parent1BreedCount ?? 0} / {MAX_BREED_COUNT}
                    </span>
                  </div>

                  <div
                    className="items-center justify-center bg-pk-gray-2 flex flex-col gap-2 w-full p-2"
                  >
                    <StrokedText strokeWidth={6} fontSize={12} className="z-10">
                      Talents
                    </StrokedText>

                    {/* Talent Slots */}
                    <div className="grid grid-cols-3 gap-1 mt-auto rounded-sm bg-pk-gray-2">
                      {parent1.talents.map(
                        (talent: string, index: number) => (
                          <PokemonTalent
                            key={`talent-${talent}-${index}`}
                            talent={talent}
                            width={20}
                            height={20}
                          />
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full bg-pk-black rounded-xl "></div>
              )}

              {/* Parent 2 */}
              {parent2 ? (
                <div className=" relative flex z-10 flex-col h-full items-start gap-2 overflow-visible   grow">
                  <div className="relative rounded-sm  h-[150px] flex w-full  items-center justify-center">
                    <PokemonSprite
                      pokemon={parent2}
                      style={{ scale: 3 }}
                      className="mt-auto z-20 translate-y-[-5px]"
                      flipHorizontal={true}
                    />
                    <div className="absolute top-0 right-0 ">
                      <StrokedText
                        fontSize={22}
                        strokeWidth={0}
                        fillColor="#ead7d1"
                        className="ml-2 my-1"
                      >
                        {`#${parent2.display_id}`}
                      </StrokedText>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-center w-full">
                    <span className="text-pk-gray-3 text-xs">BREED COUNT</span>
                    <span className="font-semibold text-pk-gray-4">
                      {breedCost?.parent2BreedCount ?? 0} / {MAX_BREED_COUNT}
                    </span>
                  </div>

                  <div
                    className="items-center justify-center bg-pk-gray-2 flex flex-col gap-2 w-full p-2"
                  >
                    <StrokedText strokeWidth={6} fontSize={12} className="z-10">
                      Talents
                    </StrokedText>

                    {/* Talent Slots */}
                    <div className="grid grid-cols-3 gap-1 mt-auto rounded-sm bg-pk-gray-2">
                      {parent2.talents.map(
                        (talent: string, index: number) => (
                          <PokemonTalent
                            key={`talent-${talent}-${index}`}
                            talent={talent}
                            width={20}
                            height={20}
                          />
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full bg-pk-black rounded-xl "></div>
              )}
            </div>

            <div className="mx-auto w-1/2 border-2 border-t-0 h-8 border-pk-gray-3"></div>
            <div className="ml-auto w-1/2 border-l-2 border-pk-gray-3 h-12"></div>

            {isBreedingBlocked ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center gap-4 bg-pk-red-1/10 rounded-lg mx-4 mt-4 border-2 border-pk-red-3 border-dashed">
                <StrokedText fontSize={20} className="text-pk-red-3">
                  Breeding Unavailable
                </StrokedText>
                <p className="text-pk-gray-4 font-semibold text-sm max-w-[250px]">
                  {parent1ReachedLimit
                    ? `${
                        parent1?.name || "Parent 1"
                      } already has ${MAX_BREED_COUNT} eggs.`
                    : `${
                        parent2?.name || "Parent 2"
                      } already has ${MAX_BREED_COUNT} eggs.`}
                </p>
                <p className="text-pk-red-3 font-bold text-xs uppercase tracking-wider">
                  Maximum Offspring Reached
                </p>
              </div>
            ) : (
              <>
                {/* Amount Picker  */}
                <div className="flex bg-pk-gray-2 px-2 py-2 justify-center gap-8 mx-auto">
                  <div className="flex flex-row items-center gap-2 ">
                    <button
                      className="cursor-pointer p-0.5 group rounded-sm bg-black/20 hover:bg-black/40"
                      onClick={() => setAmount(Math.max(1, amount - 1))}
                    >
                      <CaretIcon
                        direction="down"
                        className="fill-pk-gray-5 group-hover:fill-pk-yellow-3"
                        width={32}
                        height={32}
                      />
                    </button>
                    <div className="flex flex-col relative">
                      <img
                        src="/assets/app-ui/poke-egg.png"
                        alt="Coin"
                        className="w-auto h-8"
                      />
                      <div className="absolute bottom-0 right-0">
                        <StrokedText strokeWidth={10} fontSize={16}>
                          {amount.toString()}
                        </StrokedText>
                      </div>
                    </div>
                    <button
                      className="cursor-pointer p-0.5 group rounded-sm bg-black/20 hover:bg-black/40"
                      onClick={() =>
                        setAmount(Math.min(maxAvailableAmount, amount + 1))
                      }
                    >
                      <CaretIcon
                        direction="up"
                        className="fill-pk-gray-5 group-hover:fill-pk-yellow-3"
                        width={32}
                        height={32}
                      />
                    </button>
                  </div>
                </div>

                <div className="ml-auto w-1/2 border-l-2 border-pk-gray-3 h-4"></div>

                {/* Cost Displays Container */}
                <div className="relative">
                  <LoadingOverlay
                    isVisible={isCalculating}
                    message="Calculating..."
                    spinnerColor="border-pk-yellow-3"
                  />
                  {/*PokeCoin Cost Display */}
                  <div className="relative flex flex-row w-[220px] rounded-sm items-center mx-auto gap-4 bg-pk-gray-2 px-2 py-2 justify-between">
                    <img
                      src="/assets/app-ui/poke-coin.png"
                      alt="Coin"
                      className="w-auto h-8"
                    />
                    <div className="flex flex-row items-center gap-1">
                      <p
                        className={`font-semibold text-lg ${
                          !hasInsufficientCoins
                            ? "text-pk-green-3"
                            : "text-pk-red-3"
                        }`}
                      >
                        {breedCost ? currentTotalCost.coin : "..."}
                      </p>
                      <span className="text-pk-gray-4 font-semibold text-lg">
                        /
                      </span>
                      <div className="text-pk-gray-4 font-semibold text-lg min-w-[20px] flex justify-center">
                        {pokeCoins ?? "..."}
                      </div>
                    </div>
                  </div>

                  <div className="ml-auto w-1/2 border-l-2 border-pk-gray-3 h-4"></div>

                  {/*PokeShard Cost Display */}
                  <div className="relative flex flex-row w-[220px] rounded-sm items-center mx-auto gap-4 bg-pk-gray-2 p-2 justify-between">
                    <img
                      src="/assets/app-ui/poke-shard.png"
                      alt="Coin"
                      className="w-auto h-8"
                    />
                    <div className="flex flex-row items-center gap-1">
                      <div
                        className={cn(" font-semibold text-lg text-pk-red-3", {
                          "!text-pk-green-3": !hasInsufficientShards,
                        })}
                      >
                        <div className="min-w-[20px] flex justify-center">
                          {breedCost ? currentTotalCost.shard : "..."}
                        </div>
                      </div>
                      <span className="text-pk-gray-4 font-semibold text-lg">
                        /
                      </span>
                      <p
                        className={cn(`font-semibold text-lg  text-pk-gray-4`)}
                      >
                        {pokeShards ?? "..."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="ml-auto w-1/2 border-l-2 border-pk-gray-3 h-12 mb-8 border-dashed"></div>

                {/* Shiny Breeding Warning & Confirmation */}
                {isShinyBreed && (
                  <div className="mx-4 mb-8 p-4 bg-pk-red-1/10 rounded-lg border-2 border-pk-red-3 border-dashed flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center gap-2 text-pk-red-3">
                      <span className="text-xl">⚠️</span>
                      <StrokedText fontSize={16} className="text-pk-red-3">
                        Shiny Breeding Warning
                      </StrokedText>
                    </div>

                    <div className="flex flex-col gap-2 text-sm text-pk-gray-4 font-medium leading-relaxed">
                      <p>
                        Shiny Pokemon can breed{" "}
                        <span className="font-black text-pk-red-3">
                          ONLY ONCE
                        </span>
                        .
                      </p>
                      <p>
                        The Shiny parent(s) will be{" "}
                        <span className="text-pk-red-3 font-black uppercase">
                          RELEASED PERMANENTLY
                        </span>{" "}
                        immediately after breeding.
                      </p>
                    </div>

                    <div className="bg-pk-green-3/10 p-2 rounded border border-pk-green-3/30 text-sm text-pk-green-3 font-bold flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">✨</span>
                        <span>
                          {parent1?.is_shiny && parent2?.is_shiny
                            ? "Guaranteed 2 Shiny Offspring!"
                            : "Guaranteed 1 Shiny Offspring!"}
                        </span>
                      </div>
                      <span className="text-xs font-normal opacity-80 pl-7">
                        (Any additional eggs will be normal)
                      </span>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer mt-2 group select-none">
                      <div
                        className={cn(
                          "w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200",
                          hasConfirmedRelease
                            ? "bg-pk-red-3 border-pk-red-3 shadow-cartoon-sm scale-110"
                            : "border-pk-gray-4 bg-pk-gray-2 group-hover:border-pk-red-3/50",
                        )}
                      >
                        {hasConfirmedRelease && (
                          <span className="text-white font-bold text-sm">
                            ✓
                          </span>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={hasConfirmedRelease}
                        onChange={(e) =>
                          setHasConfirmedRelease(e.target.checked)
                        }
                      />
                      <span
                        className={cn(
                          "text-xs font-bold uppercase tracking-wide transition-colors",
                          hasConfirmedRelease
                            ? "text-pk-red-3"
                            : "text-pk-gray-4 group-hover:text-pk-gray-5",
                        )}
                      >
                        I understand the consequences
                      </span>
                    </label>
                  </div>
                )}

                {/* Breeding Button */}
                <div className="flex justify-center w-full items-center gap-8">
                  <Button
                    variant={hasInsufficientFunds ? "red" : "orange"}
                    onClick={handleBreed}
                    disabled={
                      isBreeding ||
                      isCalculating ||
                      !breedCost ||
                      hasInsufficientFunds ||
                      (isShinyBreed && !hasConfirmedRelease)
                    }
                  >
                    <div className="flex flex-row items-center gap-3">
                      {!hasInsufficientFunds && (
                        <HeartIcon
                          fill="#FFFFFF"
                          width={24}
                          height={24}
                          className="fill-pk-gray-5 transition group-hover:fill-pk-yellow-3"
                        />
                      )}
                      {isBreeding
                        ? "Breeding..."
                        : isCalculating
                        ? "Calculating..."
                        : hasInsufficientFunds
                        ? "Insufficient Funds"
                        : "Let's Breed!"}
                    </div>
                  </Button>
                </div>
              </>
            )}

            <div
              className="mx-auto transition hover:text-pk-gray-4 mt-8 text-sm text-pk-gray-3 underline font-medium cursor-pointer mb-2"
              onClick={handleCancel}
            >
              Cancel
            </div>
          </div>
        </StandardFrame>
      </div>
    </section>
  );
};
